/**
 * Counter — rolling digits, ported from React Bits' `Counter`
 * (reactbits.dev/components/counter). Each place is a strip of 0–9 that
 * slides to the current digit, with a soft fade at the top and bottom so
 * the neighbours read as depth rather than clutter.
 *
 * Rebuilt on CSS transitions rather than `motion`, like `animated-tabs`:
 * the strip's `transform` is the only animated property, `ease-out` so the
 * new digit lands quickly, and reduced motion snaps. The fades are a mask,
 * so the counter sits on any background; the ink is `currentColor`.
 *
 * @module components/ui/counter
 */
import type { CSSProperties } from "react";

import { cn } from "~/lib/utils";

const EASE_OUT = "cubic-bezier(0.23, 1, 0.32, 1)";

function Digit({ digit, height }: { readonly digit: number; readonly height: number }) {
  return (
    <span
      className="relative inline-block w-[1ch] overflow-hidden tabular-nums"
      style={{ height }}
      aria-hidden="true"
    >
      <span
        className="absolute inset-x-0 top-0 flex flex-col motion-reduce:transition-none"
        style={{
          transform: `translateY(${-digit * height}px)`,
          transitionProperty: "transform",
          transitionDuration: "600ms",
          transitionTimingFunction: EASE_OUT,
        }}
      >
        {Array.from({ length: 10 }, (_, n) => (
          <span key={n} className="flex items-center justify-center" style={{ height }}>
            {n}
          </span>
        ))}
      </span>
    </span>
  );
}

export interface CounterProps {
  /** A non-negative integer. */
  readonly value: number;
  /** How many digits to show, left-padded with zeros. */
  readonly places?: number;
  /** Font size in px; the strip's row height follows it. */
  readonly fontSize?: number;
  /** Extra vertical room per digit row, in px. */
  readonly padding?: number;
  /** Height of the top and bottom fades, in px. */
  readonly gradientHeight?: number;
  readonly className?: string;
  readonly style?: CSSProperties;
  /** Spoken value — the digits themselves are decorative. */
  readonly label?: string;
}

export function Counter({
  value,
  places,
  fontSize = 32,
  padding = 6,
  gradientHeight = 10,
  className,
  style,
  label,
}: CounterProps) {
  const height = fontSize + padding;
  const safe = Math.max(0, Math.floor(value));
  const text = String(safe).padStart(places ?? 1, "0");
  const digits = [...text].map((ch) => Number(ch));
  const mask = `linear-gradient(to bottom, transparent, black ${gradientHeight}px, black calc(100% - ${gradientHeight}px), transparent)`;
  return (
    <span
      role="img"
      aria-label={label ?? String(safe)}
      className={cn("inline-flex overflow-hidden leading-none", className)}
      style={{ fontSize, direction: "ltr", maskImage: mask, WebkitMaskImage: mask, ...style }}
    >
      {digits.map((digit, index) => (
        <Digit key={`${digits.length}-${index}`} digit={digit} height={height} />
      ))}
    </span>
  );
}
